import React, { useEffect, useState } from 'react';
import Button from './Button';

/**
 * ConfirmDialog — preflight confirmation modal for high-impact admin
 * actions. Pre-flight = re-read the implications BEFORE the action fires,
 * so a tired admin doesn't click "Lock calibration" when they meant
 * "Compute distribution".
 *
 * Three tones:
 *   • info     (light blue)   — non-destructive but consequential
 *   • warning  (amber)        — important, reversible
 *   • danger   (red)          — irreversible or affects many people
 *
 * Optional `requireTypedConfirmation` makes the user type a specific
 * phrase before the confirm button enables — used for irreversible
 * destructive actions like cycle close, calibration lock.
 *
 * Renders nothing when `open` is false, so it's safe to mount permanently
 * in a page. Cancel / Esc closes; Confirm fires the callback then closes.
 */

export type ConfirmTone = 'info' | 'warning' | 'danger';

export interface ConfirmAction {
  /** Brief bullet describing what will happen. */
  label: string;
  /** Optional supporting detail rendered as muted text. */
  detail?: string;
}

export interface ConfirmDialogProps {
  open: boolean;
  /** Modal title (action verb, e.g. "Lock calibration"). */
  title: string;
  /** Plain-text intro paragraph. */
  body?: string;
  /** Bullet-list of side-effects this action will cause. */
  actions?: ConfirmAction[];
  /** Tone — drives icon + accent colour. */
  tone?: ConfirmTone;
  /** Confirm button label. Default: "Confirm". */
  confirmLabel?: string;
  /** Cancel button label. Default: "Cancel". */
  cancelLabel?: string;
  /** When set, the user must type this string EXACTLY before confirm enables. */
  requireTypedConfirmation?: string;
  /** Loading state on the confirm button. */
  loading?: boolean;
  onCancel:  () => void;
  onConfirm: () => void;
}

const TONE: Record<ConfirmTone, { icon: string; iconCard: string; ring: string }> = {
  info:    { icon: 'info',     iconCard: 'bg-blue-50  text-blue-700',  ring: '' },
  warning: { icon: 'warning',  iconCard: 'bg-amber-50 text-amber-700', ring: '' },
  danger:  { icon: 'priority_high', iconCard: 'bg-red-50 text-red-700', ring: '' },
};

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open, title, body, actions, tone = 'warning',
  confirmLabel = 'Confirm', cancelLabel = 'Cancel',
  requireTypedConfirmation, loading,
  onCancel, onConfirm,
}) => {
  const [typed, setTyped] = useState('');

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) onCancel();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, loading, onCancel]);

  // Reset typed-confirmation when modal re-opens
  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  if (!open) return null;

  const t = TONE[tone];
  const typedOk = !requireTypedConfirmation
    || typed.trim() === requireTypedConfirmation.trim();
  const canConfirm = typedOk && !loading;

  return (
    <div
      className="fixed inset-0 z-50 bg-stone-900/40 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
    >
      <div className="bg-white rounded-2xl max-w-md w-full shadow-xl overflow-hidden">
        <div className="p-6">
          <div className="flex items-start gap-3 mb-3">
            <span className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${t.iconCard}`}>
              <span className="material-symbols-rounded" style={{ fontSize: 22 }} aria-hidden="true">
                {t.icon}
              </span>
            </span>
            <div className="min-w-0 flex-1">
              <h3 id="confirm-title" className="text-base font-semibold text-stone-900">{title}</h3>
              {body && <p className="text-sm text-stone-600 mt-1 leading-relaxed">{body}</p>}
            </div>
          </div>

          {actions && actions.length > 0 && (
            <ul className="mt-3 space-y-1.5 rounded-lg bg-stone-50 border border-stone-200 p-3">
              {actions.map((a, idx) => (
                <li key={idx} className="text-sm text-stone-800">
                  <div className="flex items-start gap-2">
                    <span className="material-symbols-rounded text-stone-400 mt-0.5 flex-shrink-0" style={{ fontSize: 14 }}>
                      check_circle
                    </span>
                    <div className="min-w-0">
                      <div>{a.label}</div>
                      {a.detail && <div className="text-xs text-stone-500 mt-0.5">{a.detail}</div>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {requireTypedConfirmation && (
            <div className="mt-4">
              <label className="block text-xs font-medium text-stone-700 mb-1">
                Type <code className="font-semibold text-stone-900">{requireTypedConfirmation}</code> to confirm:
              </label>
              <input
                type="text"
                value={typed}
                onChange={e => setTyped(e.target.value)}
                placeholder={requireTypedConfirmation}
                disabled={loading}
                className={[
                  'w-full rounded-md border bg-white px-3 py-2 text-sm',
                  'focus:outline-none focus:ring-1 focus:ring-stone-500',
                  typedOk ? 'border-stone-300' : 'border-amber-300',
                ].join(' ')}
              />
            </div>
          )}
        </div>

        <div className="border-t border-stone-200 bg-stone-50 px-6 py-3 flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={loading}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'submit' : 'primary'}
            onClick={onConfirm}
            disabled={!canConfirm}
            loading={loading}
            className={tone === 'danger' ? '!bg-red-700 hover:!bg-red-800' : ''}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
